import React, { useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Alert, Card, Empty, Row, Col, Typography, Space, Flex, Grid } from "antd";
import { useTranslation } from "react-i18next";
import { useAntdTheme, useReconnectingWebSocket } from "../../shared/hooks";

import {
  capitalizeFirstLetter,
  getOrderState,
  getOrderTypeDisplay,
  DONE_ORDER_TYPE_COLORS,
  WS_URL,
} from "../../shared/utils/Util";

const { Title, Text } = Typography;

const maxDoneOrders = 100;

// delta is an OrderUpdate object from the backend.
// https://github.com/TicketRails/backend/blob/8dfdecdf5c272c2969384e468a0ab6ee0ae8cb3c/services/api/websocket/schema.go#L9
const updateDoneOrdersByType = (oldDoneOrdersByType, delta) => {
  var newDoneOrdersByType = { ...oldDoneOrdersByType };

  // Get all the order IDs that changed
  var changedIDs = new Set();
  if (delta.updated) {
    delta.updated.forEach((order) => {
      changedIDs.add(order.id);
    });
  }
  if (delta.deleted) {
    delta.deleted.forEach((id) => changedIDs.add(id));
  }

  // Remove all changed orders from the new state.
  Object.keys(newDoneOrdersByType).forEach((orderType) => {
    newDoneOrdersByType[orderType] = newDoneOrdersByType[orderType].filter(
      (order) => !changedIDs.has(order.id)
    );
  });

  // Add all updated done orders.
  if (delta.updated) {
    delta.updated.forEach((order) => {
      const k = getOrderState(order);
      if (k === "done") {
        const lowerOrderType = order.pos_order_type.toLowerCase();
        const orderCopy = {
          id: order.id,
          pos_order_name: order.pos_order_name,
          pos_order_id: order.pos_order_id,
          done_time: order.done_time,
          payment_processor: order.payment_processor,
        };

        // Online orders (Stripe, Square online, Redeem)
        if (
          lowerOrderType === "stripe" ||
          lowerOrderType === "square" ||
          lowerOrderType === "redeem"
        ) {
          newDoneOrdersByType["online"].unshift(orderCopy);
        }
        // DoorDash (substring match for variants like "DoorDash - Delivery")
        else if (lowerOrderType.includes("doordash") || lowerOrderType.includes("door dash")) {
          newDoneOrdersByType["doordash"].unshift(orderCopy);
        }
        // UberEats (substring match for variants like "UberEats Pickup")
        else if (lowerOrderType.includes("ubereats") || lowerOrderType.includes("uber eats")) {
          newDoneOrdersByType["ubereats"].unshift(orderCopy);
        }
        // Grubhub (substring match for variants like "Grubhub - Delivery")
        else if (lowerOrderType.includes("grubhub")) {
          newDoneOrdersByType["grubhub"].unshift(orderCopy);
        }
        // In-Store orders (Clover POS/Kiosk, Square POS)
        else if (
          order.payment_processor?.toUpperCase() === "CLOVER" ||
          lowerOrderType === "to go" ||
          lowerOrderType === "togo" ||
          lowerOrderType === "to-go"
        ) {
          newDoneOrdersByType["in_store"].unshift(orderCopy);
        }
        // Others (Phone, Grubhub, etc.)
        else {
          // Extract phone number last 4 digits for phone orders
          if (lowerOrderType === "phone" || lowerOrderType === "phone order") {
            const phoneMatch = order.notes.match(
              /(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?(\d{4})/
            );
            newDoneOrdersByType["others"].unshift({
              ...orderCopy,
              notes: phoneMatch ? `Phone number last 4: ${phoneMatch[2]}` : "",
            });
          } else {
            newDoneOrdersByType["others"].unshift(orderCopy);
          }
        }
      }
    });
  }

  // Sort all done orders by done time in reverse
  Object.keys(newDoneOrdersByType).forEach((orderType) => {
    newDoneOrdersByType[orderType].sort((order1, order2) => {
      if (order1.done_time !== order2.done_time) {
        return -1 * (Date.parse(order1.done_time) - Date.parse(order2.done_time));
      }
      return order1.id.localeCompare(order2.id);
    });
  });

  // Limit each order type to maxDoneOrders
  Object.keys(newDoneOrdersByType).forEach((orderType) => {
    if (newDoneOrdersByType[orderType].length > maxDoneOrders) {
      newDoneOrdersByType[orderType] = newDoneOrdersByType[orderType].slice(0, maxDoneOrders);
    }
  });

  return newDoneOrdersByType;
};

const WebsocketDisconnectBanner: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  return (
    <Alert
      message={t("unable_to_connect")}
      description={t("retrying")}
      type="error"
      showIcon
      style={{
        marginBottom: token.marginLG,
      }}
    />
  );
};

const Component: React.FC<any> = () => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const screens = Grid.useBreakpoint();
  const restaurantId = sessionStorage.getItem("restaurant_id");
  if (!restaurantId) {
    const location = useLocation();
    return <Navigate to="/login" state={{ from: location }} />;
  }

  // Responsive font sizes based on breakpoints
  const getFontSize = (mobile: number, tablet: number, desktop: number) => {
    if (screens.xl) return `${desktop}px`;
    if (screens.md) return `${tablet}px`;
    return `${mobile}px`;
  };

  // Done orders organized by online, doordash, ubereats, grubhub, in_store, others
  const [doneOrdersByType, setDoneOrdersByType] = useState({
    online: [],
    doordash: [],
    ubereats: [],
    grubhub: [],
    in_store: [],
    others: [],
  });
  const latestUpdateRef = useRef(null);

  const { disconnected: websocketDisconnect } = useReconnectingWebSocket({
    url: `${WS_URL}/v1/ws/r/${restaurantId}/orders?init_state=done`,
    onOpen: () => {
      // Clear state since the backend only returns the latest state, and our
      // local state could be very stale if we were disconnected.
      setDoneOrdersByType({
        online: [],
        doordash: [],
        ubereats: [],
        grubhub: [],
        in_store: [],
        others: [],
      });
    },
    onMessage: (message) => {
      latestUpdateRef.current = Date.parse(message["ts"]);
      setDoneOrdersByType((prev) => updateDoneOrdersByType(prev, message));
    },
  });

  const renderOrderTitle = (order) =>
    `Order ${order.pos_order_name ? order.pos_order_name : order.pos_order_id}`;

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {/* Page Header */}
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Title
          level={1}
          style={{
            fontSize: getFontSize(32, 56, 96),
            fontWeight: token.fontWeightStrong,
            margin: 0,
            lineHeight: 1.2,
          }}
        >
          {t("ready_for_pickup")}
        </Title>
        <Text
          style={{
            fontSize: getFontSize(20, 36, 60),
            color: token.colorTextSecondary,
          }}
        >
          {t("sorted_by_done_time")}
        </Text>
      </Space>

      {/* Websocket Disconnect Banner */}
      {websocketDisconnect && <WebsocketDisconnectBanner />}

      {/* Done Orders Grid - Responsive */}
      <Row gutter={[16, 16]}>
        {Object.entries(doneOrdersByType).map(([orderType, orders]) => {
          // Get translated column name
          const getColumnName = (type: string) => {
            switch (type) {
              case "online":
                return t("online");
              case "doordash":
                return "DoorDash";
              case "ubereats":
                return "UberEats";
              case "grubhub":
                return "Grubhub";
              case "in_store":
                return t("in_store");
              case "others":
                return t("others");
              default:
                return capitalizeFirstLetter(type);
            }
          };

          // Get logo for delivery service columns
          const deliveryConfig = getOrderTypeDisplay(orderType);

          return (
            <Col
              key={orderType}
              xs={24} // 1 column on mobile
              sm={24} // 1 column on small tablet
              md={{ flex: "1" }} // 5 equal columns on iPad and larger (≥768px)
            >
              <Flex
                vertical
                style={{
                  backgroundColor: DONE_ORDER_TYPE_COLORS[orderType.toLowerCase()],
                  minHeight: "400px",
                  borderRadius: token.borderRadiusLG,
                }}
              >
                {/* Column Heading */}
                <div
                  style={{
                    textAlign: "center",
                    paddingTop: token.paddingLG,
                    fontSize: getFontSize(20, 35, 65),
                    fontWeight: token.fontWeightStrong,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  {deliveryConfig?.logo && (
                    <img
                      src={deliveryConfig.logo}
                      alt={getColumnName(orderType)}
                      style={{
                        height: getFontSize(20, 35, 65),
                        width: "auto",
                      }}
                    />
                  )}
                  {getColumnName(orderType)}
                </div>

                {/* Orders List */}
                {orders.length > 0 ? (
                  <Space
                    direction="vertical"
                    size="middle"
                    style={{ width: "100%", padding: token.paddingMD }}
                  >
                    {orders.map((order) => (
                      <Card
                        key={order.id}
                        style={{
                          boxShadow: token.boxShadowSecondary,
                        }}
                        styles={{
                          body: {
                            padding: token.paddingMD,
                          },
                        }}
                      >
                        <Text strong style={{ fontSize: getFontSize(18, 32, 50) }}>
                          {order.pos_order_name ? order.pos_order_name : order.pos_order_id}
                        </Text>
                        {order.notes && (
                          <Text
                            style={{
                              fontSize: getFontSize(16, 26, 40),
                              marginLeft: token.marginXS,
                            }}
                          >
                            {" - "}
                            {order.notes}
                          </Text>
                        )}
                      </Card>
                    ))}
                  </Space>
                ) : (
                  <Flex
                    justify="center"
                    align="center"
                    style={{
                      backgroundColor: token.colorBgContainer,
                      padding: token.paddingLG,
                      margin: token.marginMD,
                      borderRadius: token.borderRadius,
                    }}
                  >
                    <Empty description={t("no_done_orders")} />
                  </Flex>
                )}
              </Flex>
            </Col>
          );
        })}
      </Row>
    </Space>
  );
};

export default Component;
